import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../config';
import { collection, addDoc, doc, updateDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import './TimeTracker.css';

interface TimeEntry {
  id?: string;
  userId: string;
  taskId?: string;
  taskName: string;
  startTime: Date;
  endTime?: Date;
  duration: number; // in seconds
  isPaused: boolean;
  createdAt: Date;
}

interface TimeTrackerProps {
  taskId?: string;
  taskName?: string;
  onTimeUpdate?: (seconds: number) => void;
}

const TimeTracker: React.FC<TimeTrackerProps> = ({ taskId, taskName, onTimeUpdate }) => {
  const { user } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentEntry, setCurrentEntry] = useState<TimeEntry | null>(null);
  const [todayTotal, setTodayTotal] = useState(0);
  const [weekTotal, setWeekTotal] = useState(0);
  const [mode, setMode] = useState<'normal' | 'pomodoro'>('normal');
  const [pomodoroPhase, setPomodoroPhase] = useState<'work' | 'break'>('work');
  const [pomodoroCount, setPomodoroCount] = useState(0);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<Date | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const POMODORO_WORK_TIME = 25 * 60; // 25 minutes
  const POMODORO_SHORT_BREAK = 5 * 60; // 5 minutes
  const POMODORO_LONG_BREAK = 15 * 60; // 15 minutes

  useEffect(() => {
    loadTodayStats();
    loadWeekStats();
    
    // Create audio for notifications
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGWi77eefTRAMUKfj8LZjHAY4ktfyzHksBSR3x/DdkEAKFF606+uoVRQKRp/g8r5sIQUrgc7y2Yk2CBlou+3nn00QDFCn4/C2YxwGOJLX8sx5LAUkd8fw3ZBAC');
  }, [user?.uid]);

  useEffect(() => {
    if (onTimeUpdate) {
      onTimeUpdate(elapsedTime);
    }
  }, [elapsedTime, onTimeUpdate]);

  const loadTodayStats = async () => {
    if (!user?.uid) return;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const q = query(
        collection(db, 'timeEntries'),
        where('userId', '==', user.uid),
        where('createdAt', '>=', today)
      );

      const snapshot = await getDocs(q);
      let total = 0;
      snapshot.docs.forEach(doc => {
        const entry = doc.data() as TimeEntry;
        total += entry.duration || 0;
      });

      setTodayTotal(total);
    } catch (error) {
      console.error('Error loading today stats:', error);
    }
  };

  const loadWeekStats = async () => {
    if (!user?.uid) return;

    try {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const q = query(
        collection(db, 'timeEntries'),
        where('userId', '==', user.uid),
        where('createdAt', '>=', weekAgo)
      );

      const snapshot = await getDocs(q);
      let total = 0;
      snapshot.docs.forEach(doc => {
        const entry = doc.data() as TimeEntry;
        total += entry.duration || 0;
      });

      setWeekTotal(total);
    } catch (error) {
      console.error('Error loading week stats:', error);
    }
  };

  const startTimer = async () => {
    if (!user?.uid) return;

    startTimeRef.current = new Date();
    setIsRunning(true);
    setIsPaused(false);

    const entry: TimeEntry = {
      userId: user.uid,
      taskId: taskId,
      taskName: taskName || 'Untitled Task',
      startTime: startTimeRef.current,
      duration: 0,
      isPaused: false,
      createdAt: new Date()
    };

    try {
      const docRef = await addDoc(collection(db, 'timeEntries'), entry);
      setCurrentEntry({ ...entry, id: docRef.id });
    } catch (error) {
      console.error('Error starting timer:', error);
    }

    intervalRef.current = setInterval(() => {
      setElapsedTime(prev => {
        const newTime = prev + 1;
        
        // Check Pomodoro completion
        if (mode === 'pomodoro') {
          const targetTime = pomodoroPhase === 'work' ? POMODORO_WORK_TIME : 
                            (pomodoroCount % 4 === 3 ? POMODORO_LONG_BREAK : POMODORO_SHORT_BREAK);
          
          if (newTime >= targetTime) {
            handlePomodoroComplete();
          }
        }
        
        return newTime;
      });
    }, 1000);
  };

  const pauseTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPaused(true);
  };

  const resumeTimer = () => {
    setIsPaused(false);
    intervalRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  };

  const stopTimer = async () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (currentEntry && currentEntry.id) {
      try {
        const entryRef = doc(db, 'timeEntries', currentEntry.id);
        await updateDoc(entryRef, {
          endTime: new Date(),
          duration: elapsedTime
        });

        // Update stats
        await loadTodayStats();
        await loadWeekStats();
        
        // Award points for time tracked
        await awardPointsForTime(elapsedTime);
      } catch (error) {
        console.error('Error stopping timer:', error);
      }
    }

    setIsRunning(false);
    setIsPaused(false);
    setElapsedTime(0);
    setCurrentEntry(null);
    startTimeRef.current = null;
  };

  const awardPointsForTime = async (seconds: number) => {
    if (!user?.uid) return;

    try {
      const statsRef = doc(db, 'userStats', user.uid);
      const statsSnap = await getDocs(query(collection(db, 'userStats'), where('userId', '==', user.uid)));
      
      if (!statsSnap.empty) {
        const currentStats = statsSnap.docs[0].data();
        const minutes = Math.floor(seconds / 60);
        const pointsEarned = minutes * 2; // 2 points per minute

        await updateDoc(statsRef, {
          totalTimeSpent: (currentStats.totalTimeSpent || 0) + minutes,
          points: (currentStats.points || 0) + pointsEarned
        });
      }
    } catch (error) {
      console.error('Error awarding points:', error);
    }
  };

  const handlePomodoroComplete = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }

    if (pomodoroPhase === 'work') {
      setPomodoroCount(prev => prev + 1);
      setPomodoroPhase('break');
      alert(`🎉 Pomodoro complete! Take a ${pomodoroCount % 4 === 3 ? '15' : '5'} minute break!`);
    } else {
      setPomodoroPhase('work');
      alert('Break time over! Ready for another Pomodoro?');
    }

    stopTimer();
  };

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getTargetTime = () => {
    if (mode === 'normal') return null;
    return pomodoroPhase === 'work' ? POMODORO_WORK_TIME : 
           (pomodoroCount % 4 === 3 ? POMODORO_LONG_BREAK : POMODORO_SHORT_BREAK);
  };

  const getProgress = () => {
    const target = getTargetTime();
    if (!target) return 0;
    return (elapsedTime / target) * 100;
  };

  return (
    <div className="time-tracker">
      <div className="tracker-header">
        <h3>⏱️ Time Tracker</h3>
        <div className="mode-toggle">
          <button 
            className={`mode-btn ${mode === 'normal' ? 'active' : ''}`}
            onClick={() => setMode('normal')}
          >
            Normal
          </button>
          <button 
            className={`mode-btn ${mode === 'pomodoro' ? 'active' : ''}`}
            onClick={() => setMode('pomodoro')}
          >
            🍅 Pomodoro
          </button>
        </div>
      </div>

      {mode === 'pomodoro' && (
        <div className="pomodoro-info">
          <div className={`phase-badge ${pomodoroPhase}`}>
            {pomodoroPhase === 'work' ? '💼 Work Time' : '☕ Break Time'}
          </div>
          <div className="pomodoro-count">
            Session {pomodoroCount + 1} • {pomodoroCount % 4 === 3 ? 'Long break next!' : `${3 - (pomodoroCount % 4)} until long break`}
          </div>
        </div>
      )}

      <div className="timer-display">
        <div className="time-text">{formatTime(elapsedTime)}</div>
        {mode === 'pomodoro' && (
          <div className="progress-ring">
            <svg width="200" height="200">
              <circle
                cx="100"
                cy="100"
                r="90"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="10"
              />
              <circle
                cx="100"
                cy="100"
                r="90"
                fill="none"
                stroke={pomodoroPhase === 'work' ? '#667eea' : '#48bb78'}
                strokeWidth="10"
                strokeDasharray={`${2 * Math.PI * 90}`}
                strokeDashoffset={`${2 * Math.PI * 90 * (1 - getProgress() / 100)}`}
                transform="rotate(-90 100 100)"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
          </div>
        )}
      </div>

      <div className="timer-controls">
        {!isRunning ? (
          <button className="control-btn start" onClick={startTimer}>
            ▶️ Start
          </button>
        ) : (
          <>
            {!isPaused ? (
              <button className="control-btn pause" onClick={pauseTimer}>
                ⏸️ Pause
              </button>
            ) : (
              <button className="control-btn resume" onClick={resumeTimer}>
                ▶️ Resume
              </button>
            )}
            <button className="control-btn stop" onClick={stopTimer}>
              ⏹️ Stop
            </button>
          </>
        )}
      </div>

      <div className="time-stats">
        <div className="stat-box">
          <div className="stat-icon">📅</div>
          <div className="stat-info">
            <div className="stat-label">Today</div>
            <div className="stat-value">{formatDuration(todayTotal)}</div>
          </div>
        </div>
        <div className="stat-box">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <div className="stat-label">This Week</div>
            <div className="stat-value">{formatDuration(weekTotal)}</div>
          </div>
        </div>
      </div>

      {taskName && (
        <div className="current-task">
          <span className="task-label">Tracking:</span>
          <span className="task-name">{taskName}</span>
        </div>
      )}
    </div>
  );
};

export default TimeTracker;

